import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import ConfigPageLayout from '../components/ConfigPageLayout'

export default function SendGridTenantConfigPage() {
  const [apiKeyInput, setApiKeyInput] = useState('')
  const [hasKey, setHasKey] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await api.get('/settings/me')
        setHasKey(Boolean(data.has_sendgrid_api_key))
        setApiKeyInput('')
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Erreur chargement paramètres')
      }
      setLoading(false)
    })()
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = apiKeyInput.trim()
    if (!trimmed) {
      setError('Saisissez une clé API pour l’enregistrer, ou utilisez « Supprimer la clé » pour retirer la clé actuelle.')
      return
    }
    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      const { data } = await api.put('/settings/me', { sendgrid_api_key: trimmed })
      setHasKey(Boolean(data.has_sendgrid_api_key))
      setApiKeyInput('')
      setMessage('Clé API SendGrid enregistrée. Vous pouvez authentifier le domaine d’envoi depuis la page Email.')
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur sauvegarde paramètres')
    } finally {
      setSaving(false)
    }
  }

  const clearKey = async () => {
    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      const { data } = await api.put('/settings/me', { sendgrid_api_key: '' })
      setHasKey(Boolean(data.has_sendgrid_api_key))
      setApiKeyInput('')
      setMessage('Clé SendGrid supprimée.')
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur lors de la suppression')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <ConfigPageLayout title="SENDGRID" description="SendGrid pour les réponses email de vos agents" backTo="/config">
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </ConfigPageLayout>
    )
  }

  return (
    <ConfigPageLayout
      title="SENDGRID"
      description="Clé API SendGrid de votre organisation : utilisée uniquement pour l’envoi des réponses email liées à vos agents (pas pour les emails système Totemium)."
      backTo="/config"
    >
      <form onSubmit={submit} className="space-y-6">
        <section className="space-y-6 rounded-3xl border border-slate-200/80 bg-white/90 p-8 shadow-xl shadow-slate-500/10 dark:border-slate-700/60 dark:bg-zinc-950/90">
          <div className="flex items-center gap-3 border-b border-slate-200/60 pb-4 dark:border-slate-700/50">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800/80">
              <svg className="h-6 w-6 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Clé API SendGrid (tenant)</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Domain authentication et envoi des réponses depuis vos connexions email /agents
              </p>
            </div>
          </div>

          {hasKey && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              Une clé est enregistrée. Saisissez une nouvelle clé pour la remplacer, ou supprimez-la ci-dessous.
            </p>
          )}

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">API Key</label>
              <input
                type="password"
                className="w-full rounded-xl border border-zinc-200 bg-white/80 px-4 py-2.5 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 dark:border-zinc-700 dark:bg-zinc-950/70"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder={hasKey ? 'SG.xxx… (nouvelle clé pour remplacer)' : 'SG.xxx…'}
                disabled={saving}
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                Créez une clé avec les permissions d’envoi et de domain authentication sur{' '}
                <a
                  href="https://app.sendgrid.com/settings/api_keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 underline hover:text-emerald-500 dark:text-emerald-400"
                >
                  SendGrid
                </a>
                .
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-full bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/25 transition hover:bg-emerald-500 disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              {hasKey && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={clearKey}
                  className="rounded-full border border-zinc-300 bg-white px-6 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  Supprimer la clé
                </button>
              )}
            </div>
          </div>
        </section>

        {message && (
          <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/90 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-100">
            {message}
          </div>
        )}
        {error && (
          <div className="rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-900 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100">
            {error}
          </div>
        )}
      </form>
    </ConfigPageLayout>
  )
}
